// src/services/donationRequestService.ts
import axios, { AxiosHeaders, AxiosRequestHeaders } from "axios";
import { API_BASE_URL } from "../config";
import { getAuthToken } from "./authService";
import { asErr } from "../utils/apiHelpers";

/**
 * Create a local axios instance for this service.
 * Matches the pattern used elsewhere: baseURL + automatic Authorization header
 * + normalized 401 errors. Keeps each service self-contained and testable.
 */
// Use relative URLs in development (goes through proxy) or absolute in production
const getBaseURL = () => {
  if (process.env.NODE_ENV === 'development') {
    return ''; // Use relative URLs to go through setupProxy.js
  }
  return API_BASE_URL;
};

const http = axios.create({
  baseURL: getBaseURL(),
  headers: { Accept: "application/json" },
  validateStatus: (s) => s >= 200 && s < 300, // only 2xx resolve
  timeout: 60000, // 60 seconds timeout
});

// Inject JWT into Authorization header for every request.
http.interceptors.request.use((config) => {
  try {
    const tok = getAuthToken?.();
    if (tok) {
      const h = (config.headers ?? {}) as AxiosRequestHeaders | AxiosHeaders;
      (h as any).Authorization = `Bearer ${tok}`;
      config.headers = h as any;
    }
  } catch {
    /* ignore – we just skip adding the header */
  }
  return config;
});

// Normalize 401s so callers can redirect to login.
http.interceptors.response.use(
  (resp) => resp,
  (error) => {
    if (error?.response?.status === 401) {
      const e = new Error("AUTH_401");
      (e as any).code = "AUTH_401";
      throw e;
    }
    throw error;
  }
);


/* ===================== Types ===================== */

export interface CreateDonationRequestInput {
  /** Optional NIN if you choose to pass it along; BE will ignore if null */
  nationalId?: string | number | null;
  /** HMAC token for donor id (donorIdToken = base64url(donorId).base64url(userId).sig) */
  donorIdToken: string;
  donationTypeId: number;
  landId: number;
  /** We set both requestedAmount and finalAmount to the land price for now */
  requestedAmount: number;
  finalAmount: number;
}

export interface DonationRequestCreated {
  id: number;
  code: string;
  donorIdToken?: string;  // normally not returned; present only if your BE adds it
  donorName?: string;
  donationTypeId?: number;
  donationTypeName?: string;
  landId: number;
  landCode?: string;
  requestedAmount: number;
  finalAmount: number;
  state?: string;
  stageId?: number | null;
  stageName?: string | null;
  status?: number;
  message?: string;
}

/* ===================== API ===================== */

/**
 * POST /api/donation/request
 * Creates a donation request. Server decodes donorIdToken and binds it to the JWT user.
 * We read the response as text and JSON.parse it ourselves to avoid axios trying to
 * parse HTML error bodies (which can happen if Odoo proxies bubble up an HTML error).
 */
export async function createDonationRequest(
  input: CreateDonationRequestInput
): Promise<DonationRequestCreated> {
  try {
    const { data } = await http.post("/api/donation/request", input, {
      headers: { "Content-Type": "application/json" },
      responseType: "text",
      transformResponse: [(d) => d], // return raw string so we control JSON.parse
    });

    let payload: any;
    try {
      payload = JSON.parse(data as string);
      
    } catch {
      throw new Error("Invalid JSON from server while creating donation request.");
    }

    return payload as DonationRequestCreated;
  } catch (e: any) {
  if (e?.message === "AUTH_401") throw e;

  const data = e?.response?.data;

  let parsed: any = data;
  if (typeof data === "string") {
    try {
      parsed = JSON.parse(data);
    } catch {
      parsed = null;
    }
  }

  const apiMessage =
    parsed?.message ??
    parsed?.raw?.message ??
    parsed?.Raw?.message ??
    "تعذر إنشاء الطلب. يرجى المحاولة مرة أخرى";

  throw new Error(apiMessage);
}
}




// --- List/get response shape (includes request token for files) ---
export type DonationRequestItem = {
  tokenReqId: string;        // backend's request token (for files)
  id?: number;
  code: string;
  donorIdToken: string;
  donorName?: string;
  donationTypeId?: number;
  donationTypeName?: string;
  landId?: number;
  landCode?: string;
  requestedAmount?: number;
  finalAmount?: number;
  state?: string;
  stageId?: number;
  stageName?: string;
};

// --- List current user's requests for a donor (optionally for a land) ---
export async function listDonationRequests(
  donorToken: string,
  landId?: number
): Promise<DonationRequestItem[]> {
  const payload: any = { donorToken };
  if (typeof landId === "number" && !Number.isNaN(landId)) payload.landId = landId;

  const { data } = await http.post("/api/donation/request/get", payload, {
    headers: { "Content-Type": "application/json" },
  });

  return Array.isArray(data) ? (data as DonationRequestItem[]) : [];
}
